import { useMemo, useRef, useState } from 'react';
import { useSettingsEmployees } from '../../lib/useSettings';
import { Dropdown } from '../design';

/**
 * Dropdown for the labor "Class" cell, shared by the rollup table and the
 * inbox labor table. Options are derived from distinct classifications
 * already in use on the project, with a small floor of common construction-
 * industry classes baked in so a fresh project isn't an empty dropdown.
 *
 * The currently-selected value is always present in the option list — if
 * the employee already has a class that's not in the floor and not yet
 * surfaced elsewhere (rare, but possible on the very first row), we add
 * it explicitly so the dropdown renders without a stray "—".
 *
 * For "I need a class that doesn't exist yet", Joey goes to Settings →
 * Employees and edits the employee directly. We keep the dropdown closed
 * to free-text entry to avoid drift / typo classifications proliferating
 * across the project.
 */
export interface InlineClassSelectProps {
  value: string;
  onChange: (next: string) => void | Promise<void>;
  disabled?: boolean;
  ariaLabel?: string;
  width?: number;
}

const STANDARD_CLASSIFICATIONS = [
  'Foreman',
  'Operator',
  'Journeyman',
  'Apprentice',
  'Laborer',
];

export function InlineClassSelect({
  value,
  onChange,
  disabled,
  ariaLabel,
  width = 130,
}: InlineClassSelectProps) {
  // Local draft so the cell can show the in-flight selection while the
  // save round-trips. The parent rebinds `value` post-refetch.
  const [draft, setDraft] = useState<string>(value);
  // The control is a button now, not a <select>, so "is the user mid-pick?"
  // is asked of THIS cell's own trigger rather than of the tag name of
  // whatever happens to be focused. Same moment, narrower question.
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Re-sync when upstream value changes (parent refetch, etc).
  if (draft !== value && document.activeElement !== triggerRef.current) {
    // Only resync when the user isn't actively editing. Otherwise their
    // in-progress pick would snap back to server state mid-interaction.
    setDraft(value);
  }

  const employees = useSettingsEmployees();
  const options = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const add = (v: string) => {
      const trimmed = v.trim();
      if (trimmed === '' || seen.has(trimmed)) return;
      seen.add(trimmed);
      out.push(trimmed);
    };
    STANDARD_CLASSIFICATIONS.forEach(add);
    (employees.data ?? []).forEach((e) => add(e.classification));
    add(value); // ensure the current value is always selectable.
    return out.map((v) => ({ value: v, label: v }));
  }, [employees.data, value]);

  return (
    <Dropdown
      ref={triggerRef}
      uiSize="sm"
      options={options}
      value={draft}
      onChange={(next) => {
        setDraft(next);
        if (next !== value) void onChange(next);
      }}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{ width }}
    />
  );
}
